import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Store, Plus } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { tenantsApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api/errors";
import { BUSINESS_TYPES, SALE_MODES, businessTypeLabel, saleModeLabel } from "@/lib/api/types";
import type { BusinessType, SaleMode, TenantDto } from "@/lib/api/types";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";

export default function TenantsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);

  const tenants = useQuery({ queryKey: ["tenants"], queryFn: tenantsApi.list });

  const toggleActive = useMutation({
    mutationFn: (tenant: TenantDto) => tenantsApi.update(tenant.id, { active: !tenant.active }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({
        title: `${updated.name} ${updated.active ? "activated" : "deactivated"}`,
        variant: "success",
      });
    },
    onError: (err) =>
      toast({ title: "Couldn't update business", description: apiErrorMessage(err), variant: "destructive" }),
  });

  const rows = (tenants.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div>
      <PageHeader
        title="Businesses"
        description="Every business onboarded on the platform — restaurants, shops, hardware, spares…"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New business
          </Button>
        }
      />

      <Card>
        {tenants.isLoading && <Skeleton className="m-5 h-48" />}
        {tenants.data && rows.length === 0 && (
          <EmptyState
            icon={Store}
            title="No businesses yet"
            description="Onboard your first business to get started."
          />
        )}
        {rows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden sm:table-cell">Business</TableHead>
                <TableHead className="hidden lg:table-cell">Address</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead className="hidden xl:table-cell">Onboarded</TableHead>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <Link
                      to={`/tenants/${tenant.id}`}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {tenant.name}
                    </Link>
                    {/* On phones the type and status columns are hidden — show them under the name. */}
                    <div className="mt-1 flex flex-wrap gap-1 sm:hidden">
                      <Badge variant="outline">{businessTypeLabel(tenant.businessType)}</Badge>
                      <Badge variant={tenant.active ? "success" : "destructive"}>
                        {tenant.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant="outline">{businessTypeLabel(tenant.businessType)}</Badge>
                    {tenant.businessType !== "RESTAURANT" && (
                      <Badge variant="outline" className="ml-1">
                        {saleModeLabel(tenant.saleMode)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground lg:table-cell">{tenant.address || "—"}</TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">{tenant.phoneNumber || "—"}</TableCell>
                  <TableCell className="hidden xl:table-cell">{formatDate(tenant.createdAt)}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={tenant.active ? "success" : "destructive"}>
                      {tenant.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmDialog
                      trigger={
                        <Button variant="outline" size="sm" disabled={toggleActive.isPending}>
                          {tenant.active ? "Deactivate" : "Activate"}
                        </Button>
                      }
                      title={tenant.active ? `Deactivate ${tenant.name}?` : `Activate ${tenant.name}?`}
                      description={
                        tenant.active
                          ? "Staff at this business won't be able to use the OSF app until it's reactivated."
                          : "Staff at this business will be able to use the OSF app again."
                      }
                      confirmLabel={tenant.active ? "Deactivate" : "Activate"}
                      confirmVariant={tenant.active ? "destructive" : "default"}
                      onConfirm={() => toggleActive.mutateAsync(tenant).then(() => undefined)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        {creating && <TenantCreateDialog onDone={() => setCreating(false)} />}
      </Dialog>
    </div>
  );
}

function TenantCreateDialog({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [businessType, setBusinessType] = useState<BusinessType>("RESTAURANT");
  const [saleMode, setSaleMode] = useState<SaleMode>("RETAIL");

  const save = useMutation({
    mutationFn: () =>
      tenantsApi.create({
        name: name.trim(),
        address: address.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        businessType,
        saleMode: businessType === "RESTAURANT" ? undefined : saleMode,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({ title: `${created.name} onboarded as ${businessTypeLabel(created.businessType)}`, variant: "success" });
      onDone();
    },
    onError: (err) =>
      toast({ title: "Couldn't create business", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle>New business</DialogTitle>
      </DialogHeader>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label>Business type</Label>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value as BusinessType)}
          >
            {BUSINESS_TYPES.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Decides what the staff app shows: restaurant floor, or shop stock &amp; sales.
          </p>
        </div>
        {businessType !== "RESTAURANT" && (
          <div className="space-y-1.5">
            <Label>Sells</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={saleMode}
              onChange={(e) => setSaleMode(e.target.value as SaleMode)}
            >
              {SALE_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              The add-stock form and the till start in this mode for the manager and sellers.
            </p>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mama Ntilie Grill"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Address (optional)</Label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Kariakoo, Dar es Salaam" />
        </div>
        <div className="space-y-1.5">
          <Label>Phone (optional)</Label>
          <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="e.g. +255 712 000 000" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={save.isPending || !name.trim()}>
            {save.isPending ? "Creating…" : "Create business"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
